import React, { type ErrorInfo, type ReactElement, type ReactNode, useCallback } from 'react';

import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { EntryPointContainer, type PreloadedEntryPoint } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';
import { useUFOReportError } from '@atlaskit/react-ufo/report-error';
import { reportError } from '@atlaskit/report-error';
import { useEntryPointConfig } from '@atlassian/entry-point-config';
import type { GetRuntimePropsFromEntryPointReference } from '@atlassian/entry-point-types';

type OnError = (error: Error, info: ErrorInfo) => void;
export type ErrorFallback = (props: {
	error: Error;
	onClose: () => void;
}) => ReactElement<any, any> | null;
export type ErrorCallback = (props: { error: Error; errorInfo: ErrorInfo }) => void;

export type Props<TPreloadedEntryPoint> = {
	entryPointReference: TPreloadedEntryPoint;
	errorFallback?: ErrorFallback;
	fallback: ReactNode;
	id: string;
	forcedError?: boolean;
	onError?: ErrorCallback;
	placeholderName?: string;
	runtimeProps: GetRuntimePropsFromEntryPointReference<TPreloadedEntryPoint>;
};

const noop = () => {};

function InternalEntryPointContainerInner<TPreloadedEntryPoint extends PreloadedEntryPoint<any>>({
	entryPointReference,
	errorFallback,
	/** Log and track errors */
	onError: onErrorLocal,
	forcedError,
	/** Loading fallback */
	fallback,
	id,
	placeholderName,
	runtimeProps,
}: Props<TPreloadedEntryPoint>) {
	const { PlaceholderComponent } = useEntryPointConfig();

	// useUFOReportError needs to be called under correct UFOSegment to work
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const reportErrorUFO = fg('entry_point_container_ufo_report_error') ? useUFOReportError() : noop;

	const onError = useCallback<OnError>(
		(error, errorInfo) => {
			onErrorLocal?.({ error, errorInfo });

			reportError({
				error,
				errorInfo,
				interactionId: id,
			});

			if (fg('entry_point_container_ufo_report_error')) {
				reportErrorUFO({
					name: id,
					errorMessage: error.message,
					errorStack: error.stack,
					forcedError,
				});
			}
		},
		[onErrorLocal, id, reportErrorUFO, forcedError],
	);

	const FallbackComponent = useCallback(
		({ error }: FallbackProps) => {
			const onClose = (runtimeProps as any)?.onClose ?? (() => {});
			return errorFallback ? errorFallback({ error, onClose }) : null;
		},
		[errorFallback, runtimeProps],
	);

	return (
		<ErrorBoundary FallbackComponent={FallbackComponent} onError={onError}>
			<PlaceholderComponent
				name={placeholderName ?? 'internal-internal-entry-point-container'}
				fallback={fallback}
			>
				<EntryPointContainer entryPointReference={entryPointReference} props={runtimeProps} />
			</PlaceholderComponent>
		</ErrorBoundary>
	);
}

export function InternalEntryPointContainer<TPreloadedEntryPoint extends PreloadedEntryPoint<any>>(
	props: Props<TPreloadedEntryPoint>,
): React.JSX.Element {
	const { id } = props;
	const { UFOSegmentComponent } = useEntryPointConfig();

	return (
		<UFOSegmentComponent name={id}>
			<InternalEntryPointContainerInner {...props} />
		</UFOSegmentComponent>
	);
}
